import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";

import BidCommentsSidebar from "../../components/bids/bid_comments_sidebar/BidCommentsSidebar";
import BidDetailsSidebar from "../../components/bids/bid_details_sidebar/BidDetailsSidebar";
import { filterBidDefaultValues } from "../../components/bids/utils";
import {
  BreadcrumbContainer,
  RouterBreadcrumb,
} from "../../components/ui/breadcrumb/SimpleBreadcrumb";
import Button from "../../components/ui/button/Button";
import EmptyState from "../../components/ui/empty_state/EmptyState";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { BidDetails } from "../../lib/hooks/api/bid/bids/useBid";
import { UpdateBidsRequestData } from "../../lib/hooks/api/bid/bids/useUpdateBids";
import { Flex } from "../../styles/utility-components";

type Props = {
  bid: BidDetails | undefined;
  isLoading: boolean;
  isError: boolean;
  id: string;
  sidebarWidth: string;
  children: React.ReactNode;
};

export type ActiveSidebarTab = "details" | "comments";

export default function BidDetailsLayout({
  id,
  bid,
  isLoading,
  isError,
  sidebarWidth,
  children,
}: Props) {
  const [activeSidebarTab, setActiveSidebarTab] = useState<ActiveSidebarTab>("details");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const methods = useForm<UpdateBidsRequestData>();

  useEffect(() => {
    if (!bid) return;
    methods.reset(filterBidDefaultValues(bid));
  }, [methods, bid]);

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center" height="100%">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this bid"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!bid) {
    return (
      <Flex flexGrow={1} alignItems="center" height="100%">
        <EmptyState
          type="loadError"
          heading="Bid not found"
          description="The bid you are looking for does not exist."
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  const handleSidebarTabChange = (tab: ActiveSidebarTab) => {
    setActiveSidebarTab(tab);
    setIsSidebarOpen(true);
  };

  return (
    <Container>
      <BreadcrumbContainer>
        <RouterBreadcrumb overrides={{ [`/bids/${id}`]: { label: bid?.name || "Bid" } }} />
        <Flex gap={4}>
          <Button
            buttonType="primary"
            buttonStyle="inverse"
            className={activeSidebarTab === "details" ? "active" : undefined}
            onClick={() => handleSidebarTabChange("details")}
          >
            Details
          </Button>
          <Button
            buttonType="primary"
            buttonStyle="inverse"
            className={activeSidebarTab === "comments" ? "active" : undefined}
            onClick={() => handleSidebarTabChange("comments")}
          >
            Comments
          </Button>
        </Flex>
      </BreadcrumbContainer>
      <FormProvider {...methods}>
        <Content>
          {children}
          <Sidebar data-open={isSidebarOpen} width={sidebarWidth}>
            {activeSidebarTab === "details" ? (
              <BidDetailsSidebar bid={bid} />
            ) : (
              <BidCommentsSidebar bidId={bid.id} bidName={bid.name} comments={bid.comments} />
            )}
          </Sidebar>
        </Content>
      </FormProvider>
    </Container>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  overflow: "hidden",
  overflowX: "hidden",
  flex: 1,
  minHeight: 0,
});

const Content = styled(Flex)({
  flexGrow: 1,
  minHeight: 0,
  flexDirection: "row",
  overflow: "hidden",
});

const Sidebar = styled(Flex)<{ width: string }>(({ width, theme }) => ({
  width: 0,
  maxWidth: 440,
  flexShrink: 0,
  flexDirection: "column",
  minHeight: 0,
  overflowY: "auto",
  transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&[data-open='true']": {
    width,
    borderLeft: `1px solid ${theme.colors.sysBorderDecorative}`,
  },

  "& > *": {
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
  },

  "&[data-open='true'] > *": {
    opacity: 1,
    transitionDelay: "0.1s",
  },
}));
